import { ComponentProps, ComponentType, Fragment, ReactNode } from 'react';

import { AttributeGrid } from 'design-system/components/Attribute';

// TODO: import this from lodash once it gets added, see
// issue here: https://github.com/lodash/lodash/issues/1379
function chunkBy<T>(array: Array<T>, predicate?: (attr: T) => boolean) {
  const chunks: Array<Array<T>> = [];
  let chunkIdx = 0;
  array.forEach((el) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (predicate(el)) {
      const chunk = chunks[chunkIdx] ?? [];
      chunks[chunkIdx] = chunk;
      chunk.push(el);
    } else {
      // If some previous elements matched the predicate,
      // we need to skip over them, hence the += 2 vs += 1
      if (chunks[chunkIdx]) {
        chunkIdx += 2;
      } else {
        chunkIdx += 1;
      }
      chunks.push([el]);
    }
  });
  return chunks;
}

/**
 * formatGridAndNonGridAttributes takes in a layout, a list of attributes which should be placed in a grid
 * and a mapping from the attribute type to a node, and returns a react node with grid attributes which are
 * beside eachother placed in a grid, and non grid attributes standing alone.
 *
 * For example, formatGridAndNonGridAttributes([attr4, attr1, attr2, attr5, attr 3], [attr1, attr2, attr3], ...)
 * will return a list of attributes formatted as follows:
 * <attr4>
 * <AttributeGrid>
 *   <attr1/>
 *   <attr2/>
 * </AttributeGrid>
 * <attr5>
 * <AttributeGrid>
 *   <attr3/>
 * </AttributeGrid>
 */
export function formatGridAndNonGridAttributes<T>(
  layout: T[],
  gridAttributes: T[],
  getAttributeByType: (attr: T) => ReactNode,
  GridAttribute: ComponentType<ComponentProps<typeof AttributeGrid>> = AttributeGrid,
) {
  const splitLayout = chunkBy(layout, (attr) => gridAttributes.includes(attr));
  return (
    <>
      {splitLayout.map((attrList, i) => {
        const isGridAttrs = gridAttributes.includes(attrList[0]);
        if (isGridAttrs) {
          return <GridAttribute key={i}>{attrList.map(getAttributeByType)}</GridAttribute>;
        } else {
          return <Fragment key={i}>{attrList.map(getAttributeByType)}</Fragment>;
        }
      })}
    </>
  );
}
